/**
 * AiTutorPage  (route: /ai-tutor)
 *
 * The headless page the egress browser loads to record an ai-tutor session.
 * Deliberately stripped to the essentials — NO socket-server, NO REST AI panels,
 * NO participant tiles / emotion / talk-time / watermark / clock. It renders:
 *   - the presentation iframe, with the CURRENT deck resolved from room
 *     metadata's `lectures` map (elementId -> URL) — no Sanity access needed
 *   - a transcript side panel (baked into the recording), mirroring the
 *     student's frontend AiTutorSession panel
 *   - the room audio (RoomAudioRenderer) so egress captures it
 *
 * It joins the LiveKit room as a muted, camera-off recorder participant, which
 * is also how the transcript panel receives `lk.transcription` + `lk.chat`.
 *
 * Stopping the recording is owned by the ai-tutor agent (it tells lms-backend on
 * lesson end / student-leave), so this page has no stop logic.
 *
 * URL params (built by lms-backend): livekitToken, wsUrl, elementId (bootstrap
 * deck so the iframe renders before the agent's first metadata write).
 */
import { useSearchParams } from 'react-router-dom';
import { LiveKitRoom, RoomAudioRenderer } from '@livekit/components-react';
import { datadogLogs } from '@datadog/browser-logs';

import { AiTutorProvider, useAiTutor } from 'contexts/AiTutorContext';
import AiTutorPresentation from 'components/AiTutorPresentation';
import AiTutorTranscript from 'components/AiTutorTranscript';
import LiveKitReconnect from 'hooks/useLiveKitReconnect';

const Stage = () => {
  const { lectures, elements, elementId, position } = useAiTutor();

  return (
    <div
      style={{ display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden' }}
    >
      {/* Slides area — all decks stay mounted (preloaded); only the active one
          is visible and driven to `position`. */}
      <div style={{ position: 'relative', flex: 1, minWidth: 0, backgroundColor: '#000000' }}>
        {elements.map((el) => {
          const url = lectures[el];
          if (!url) return null;
          const isActive = el === elementId;
          return (
            <div
              key={el}
              style={{ position: 'absolute', inset: 0, visibility: isActive ? 'visible' : 'hidden' }}
            >
              <AiTutorPresentation presentationUrl={url} position={position} active={isActive} />
            </div>
          );
        })}
      </div>

      {/* Transcript side panel — part of the recorded frame. */}
      <AiTutorTranscript />
    </div>
  );
};

const AiTutorPage = () => {
  const [searchParams] = useSearchParams();

  const livekitToken = searchParams.get('livekitToken');
  const wsUrl = searchParams.get('wsUrl');
  const elementId = searchParams.get('elementId') || undefined;

  if (!livekitToken || !wsUrl) {
    return (
      <div className="flex items-center justify-center h-screen text-white bg-black">
        <div className="text-center">
          <p className="mb-2 text-red-500">Missing AI Tutor recording parameters</p>
          <p className="text-sm">Requires livekitToken and wsUrl.</p>
        </div>
      </div>
    );
  }

  return (
    <LiveKitRoom
      serverUrl={wsUrl}
      token={livekitToken}
      connect={true}
      audio={false}
      video={false}
      onError={(error) => {
        datadogLogs.logger.error(
          'Error in AI Tutor recorder room',
          { label: 'livekit-error', source: 'recording-app', error: error.message },
          error,
        );
      }}
    >
      <AiTutorProvider bootstrapElementId={elementId}>
        <Stage />
        <RoomAudioRenderer />
        {/* Same failure mode as the class recorder: LiveKitRoom never re-dials
            after a dropped connection, so the egress would keep capturing a
            frozen page for the rest of the session. */}
        <LiveKitReconnect
          serverUrl={wsUrl}
          token={livekitToken}
          logContext={{ page: 'ai-tutor' }}
        />
      </AiTutorProvider>
    </LiveKitRoom>
  );
};

export default AiTutorPage;
